import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Lock, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../lib/auth';
import { api } from '../lib/api';

/**
 * Diálogo BLOQUEANTE que se muestra cuando el usuario tiene
 * must_change_password=true. No se puede cerrar (sin botón cancelar) y
 * exige rellenar la contraseña actual y una nueva (con confirmación) antes
 * de continuar usando la app.
 */
export default function ChangePasswordGuard() {
  const { user, updateUser, logout } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Limpiar estado al cambiar de usuario
  useEffect(() => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
  }, [user?.id]);

  if (!user || !user.must_change_password) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      return toast.error('La nueva contraseña debe tener al menos 8 caracteres');
    }
    if (newPassword !== confirmPassword) {
      return toast.error('Las contraseñas nuevas no coinciden');
    }
    if (newPassword === currentPassword) {
      return toast.error('La nueva contraseña debe ser diferente de la actual');
    }
    setSubmitting(true);
    try {
      await api.post('/api/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
      });
      updateUser({ must_change_password: false });
      toast.success('Contraseña actualizada correctamente');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al cambiar contraseña');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={() => { /* bloquea cierre */ }}>
      <DialogContent
        className="sm:max-w-md"
        // Evitar cierre con Escape o clic fuera
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        data-testid="change-password-guard-dialog"
      >
        <DialogHeader>
          <div className="flex items-center gap-3 mb-1">
            <div className="bg-[rgba(46,110,94,0.18)] text-[#13231E] rounded-full p-2">
              <Lock className="w-5 h-5" />
            </div>
            <DialogTitle>Cambia tu contraseña</DialogTitle>
          </div>
          <DialogDescription className="text-sm">
            Por seguridad, necesitas establecer una contraseña propia antes de continuar. Esto se hace una sola vez.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label>Contraseña actual</Label>
            <div className="relative">
              <Input
                type={showCurrent ? 'text' : 'password'}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                autoFocus
                data-testid="change-pwd-current"
              />
              <button
                type="button"
                onClick={() => setShowCurrent(s => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-[#13231E]"
                tabIndex={-1}
              >
                {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <Label>Nueva contraseña</Label>
            <div className="relative">
              <Input
                type={showNew ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={8}
                data-testid="change-pwd-new"
              />
              <button
                type="button"
                onClick={() => setShowNew(s => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-[#13231E]"
                tabIndex={-1}
              >
                {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Mínimo 8 caracteres. Usa algo solo tuyo.</p>
          </div>
          <div>
            <Label>Confirmar nueva contraseña</Label>
            <Input
              type={showNew ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={8}
              data-testid="change-pwd-confirm"
            />
          </div>
          <div className="flex items-center justify-between gap-2 pt-2">
            <Button
              type="button"
              variant="ghost"
              onClick={logout}
              className="text-muted-foreground"
              data-testid="change-pwd-logout"
            >
              Cerrar sesión
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="bg-[#2E6E5E] hover:bg-[#1E4A3F]"
              data-testid="change-pwd-submit"
            >
              {submitting ? 'Guardando…' : 'Guardar y continuar'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
